import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, idr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Building2, Plus, X, Search, MessageCircle, BookOpen, Pencil, Trash2,
  RefreshCw, Power, Copy, AlertTriangle, GitMerge, CheckCircle2, XCircle,
} from "lucide-react";

const TIPE_META = {
  distributor: { label: "Distributor", chip: "bg-sky-100 text-sky-800" },
  perorangan: { label: "Perorangan", chip: "bg-violet-100 text-violet-800" },
  toko_lain: { label: "Toko Lain", chip: "bg-amber-100 text-amber-800" },
};

/** Badge umur tagihan per kartu supplier (poin 8 brief owner 17 Agu 2026).
 *  `aging_bucket_terparah` dari `GET /supplier/ringkasan` hanya berisi salah
 *  satu dari 4 kunci ini (atau null kalau belum ada yang menunggak) — bucket
 *  & ambangnya DITENTUKAN backend (`_shared.AGING_BUCKETS_ORDER`, dipakai
 *  bersama oleh Piutang & Utang), di sini murni label + warna tampilan. */
const AGING_TERPARAH_META = {
  "0_30":   { label: "Menunggak 0-30 hari", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  "31_60":  { label: "Menunggak 31-60 hari", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  "61_90":  { label: "Menunggak 61-90 hari", tone: "bg-orange-50 text-orange-700 border-orange-200" },
  over_90:  { label: "Menunggak >90 hari", tone: "bg-rose-50 text-rose-700 border-rose-200" },
};

const emptyForm = () => ({
  nama: "", telepon: "", kontak_person: "", email: "",
  alamat: "", tipe: "distributor", catatan: "",
  // Lead time restock (hari, brief owner 14 Agu #8) — string kosong = belum
  // diketahui, dikonversi ke null (bukan 0) saat dikirim, lihat `simpan()`.
  lead_time_hari: "",
});

export default function Supplier() {
  const { can, user } = useAuth();
  const bolehTulis = can("inventory_po", "create");
  const bolehUbah = can("inventory_po", "update");
  const bolehHapus = can("inventory_po", "delete");
  // Sinkron mengeksekusi backfill master data — backend membatasinya ke
  // admin/superadmin, jadi tombolnya juga hanya tampil untuk mereka.
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";

  const [data, setData] = useState({ items: [], total_hutang: 0, belum_terdaftar: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [lihatNonaktif, setLihatNonaktif] = useState(false);
  const [busy, setBusy] = useState(false);

  // form: { mode: "create" } | { mode: "edit", id, punya_riwayat, ...fields }
  const [form, setForm] = useState(null);
  // drawer buku besar: { supplier, date_from, date_to, buku, loading }
  const [drawer, setDrawer] = useState(null);
  // modal merge: { sourceType: "master"|"nama", sourceId, sourceNama, targetId,
  //                preview, previewLoading, executing }
  // Backend menggerbangi merge ke admin/superadmin SAJA (require_role, bukan
  // require_permission) — operasi ini menulis ulang identitas supplier di 8
  // koleksi lintas Inventory+Finance sekaligus, tidak dibatasi satu modul.
  // Meniru gerbang "Sinkron dari Riwayat" yang sudah ada di halaman ini.
  const [mergeModal, setMergeModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/supplier/ringkasan");
      setData(r.data || { items: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar supplier");
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const items = useMemo(() => {
    const teks = q.trim().toLowerCase();
    return (data.items || []).filter(s => {
      if (!lihatNonaktif && s.is_active === false) return false;
      if (!teks) return true;
      return [s.nama, s.kontak_person, s.telepon, s.email]
        .some(v => (v || "").toLowerCase().includes(teks));
    });
  }, [data, q, lihatNonaktif]);

  const berhutang = (data.items || []).filter(s => s.hutang > 0).length;
  // Total tagihan yang sudah menunggak > 60 hari (poin 8) — padanan "total_macet"
  // di aging piutang (61-90 + >90), dijumlahkan dari `aging_summary` lintas
  // SEMUA supplier (termasuk yang belum terdaftar di master) yang dikirim backend.
  const totalMacet = (data.aging_summary || [])
    .filter(b => b.key === "61_90" || b.key === "over_90")
    .reduce((s, b) => s + (b.total || 0), 0);

  // ---- CRUD ----
  const simpan = async () => {
    setBusy(true);
    try {
      const body = {
        ...form,
        // "" (belum diisi) -> null (belum diketahui), BUKAN 0 — 0 berarti
        // klaim eksplisit "barang datang seketika".
        lead_time_hari: form.lead_time_hari === "" || form.lead_time_hari == null
          ? null : Number(form.lead_time_hari),
      };
      const r = form.mode === "edit"
        ? await api.put(`/supplier/${form.id}`, body)
        : await api.post("/supplier", body);
      toast.success(r.data.message || "Tersimpan");
      setForm(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan supplier");
    } finally { setBusy(false); }
  };

  const hapus = async (s) => {
    const tanya = s.punya_riwayat
      ? `${s.nama} punya riwayat tagihan — supplier akan DINONAKTIFKAN (bukan dihapus). Lanjut?`
      : `Hapus supplier ${s.nama}?`;
    if (!window.confirm(tanya)) return;
    setBusy(true);
    try {
      const r = await api.delete(`/supplier/${s.id}`);
      toast.success(r.data.message || "Selesai");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus supplier");
    } finally { setBusy(false); }
  };

  const aktifkan = async (s) => {
    setBusy(true);
    try {
      await api.put(`/supplier/${s.id}`, { ...s, is_active: true });
      toast.success(`${s.nama} diaktifkan lagi`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengaktifkan supplier");
    } finally { setBusy(false); }
  };

  const sinkron = async () => {
    setBusy(true);
    try {
      const r = await api.post("/supplier/sinkron");
      toast.success(r.data.message || "Sinkron selesai");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal sinkron supplier");
    } finally { setBusy(false); }
  };

  // ---- Merge Supplier (brief owner 17 Agu poin 6) ----
  // `sourceType: "nama"` menampung kasus nyata "TAM" — nama bebas yang muncul
  // di riwayat (`belum_terdaftar`) TAPI TIDAK PERNAH terdaftar di master;
  // backend menerima source_nama TANPA source_id untuk kasus itu.
  const bukaMerge = (prefill = {}) => setMergeModal({
    sourceType: prefill.sourceNama ? "nama" : "master",
    sourceId: prefill.sourceId || "",
    sourceNama: prefill.sourceNama || "",
    targetId: "",
    preview: null,
    previewLoading: false,
    executing: false,
  });

  // Ubah sumber/target APA PUN membatalkan pratinjau lama — tombol eksekusi
  // hanya boleh aktif untuk pratinjau yang benar-benar cocok dengan pilihan
  // saat ini, supaya staf tidak mengeksekusi hasil pratinjau yang sudah basi.
  const ubahMerge = (patch) => setMergeModal(m => (m ? { ...m, ...patch, preview: null } : m));

  const bodyMerge = (m) => (m.sourceType === "master"
    ? { source_id: m.sourceId, target_id: m.targetId }
    : { source_nama: m.sourceNama, target_id: m.targetId });

  const previewMerge = async () => {
    const m = mergeModal;
    if (!m) return;
    const sumberKosong = m.sourceType === "master" ? !m.sourceId : !m.sourceNama.trim();
    if (sumberKosong || !m.targetId) {
      toast.error("Pilih supplier sumber dan target dulu");
      return;
    }
    setMergeModal(cur => ({ ...cur, previewLoading: true }));
    try {
      const r = await api.post("/supplier/merge/preview", bodyMerge(m));
      setMergeModal(cur => ({ ...cur, preview: r.data, previewLoading: false }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat pratinjau merge");
      setMergeModal(cur => ({ ...cur, previewLoading: false }));
    }
  };

  const eksekusiMerge = async () => {
    const m = mergeModal;
    if (!m?.preview?.bisa_eksekusi) return;
    setMergeModal(cur => ({ ...cur, executing: true }));
    try {
      const r = await api.post("/supplier/merge/execute", bodyMerge(m));
      toast.success(r.data.message || "Supplier digabung");
      setMergeModal(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menggabungkan supplier");
      setMergeModal(cur => ({ ...cur, executing: false }));
    }
  };

  // ---- Buku besar ----
  const bukaBuku = async (s, date_from = "", date_to = "") => {
    setDrawer(d => ({ supplier: s, date_from, date_to, buku: d?.buku, loading: true }));
    try {
      const params = {};
      if (date_from) params.date_from = date_from;
      if (date_to) params.date_to = date_to;
      const r = await api.get(`/supplier/${s.id}/buku`, { params });
      setDrawer({ supplier: s, date_from, date_to, buku: r.data, loading: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat buku supplier");
      setDrawer(null);
    }
  };

  const kirimStatement = async () => {
    if (!drawer) return;
    setBusy(true);
    try {
      const params = {};
      if (drawer.date_from) params.date_from = drawer.date_from;
      if (drawer.date_to) params.date_to = drawer.date_to;
      const r = await api.get(`/supplier/${drawer.supplier.id}/statement`, { params });
      if (r.data.wa_link) {
        window.open(r.data.wa_link, "_blank", "noopener");
      } else {
        // Tanpa nomor WA statement tetap terpakai: salin teksnya ke clipboard
        // supaya bisa ditempel ke email/chat lain — tombolnya tidak buntu.
        await navigator.clipboard.writeText(r.data.text || "");
        toast.info("Supplier belum punya nomor telepon — teks statement disalin ke clipboard");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyiapkan statement");
    } finally { setBusy(false); }
  };

  const salinStatement = async () => {
    if (!drawer) return;
    setBusy(true);
    try {
      const params = {};
      if (drawer.date_from) params.date_from = drawer.date_from;
      if (drawer.date_to) params.date_to = drawer.date_to;
      const r = await api.get(`/supplier/${drawer.supplier.id}/statement`, { params });
      await navigator.clipboard.writeText(r.data.text || "");
      toast.success("Teks statement disalin ke clipboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyalin statement");
    } finally { setBusy(false); }
  };

  const buku = drawer?.buku;

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="supplier-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Inventory</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <Building2 className="w-7 h-7 text-emerald-600" /> Supplier
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Master supplier + buku hutang per supplier, diturunkan dari tagihan pembelian.
            Pembayaran tagihan tetap lewat{" "}
            <Link to="/finance/utang" data-testid="sup-link-utang"
                  className="text-emerald-700 font-medium hover:underline">Finance &gt; Utang</Link>.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <button onClick={sinkron} disabled={busy} data-testid="sup-sinkron"
                    className="h-10 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2 disabled:opacity-50">
              <RefreshCw className="w-4 h-4" /> Sinkron dari Riwayat
            </button>
          )}
          {isAdmin && (
            <button onClick={() => bukaMerge()} disabled={busy} data-testid="sup-merge-open"
                    title="Gabungkan dua supplier duplikat (mis. 'TAM' ke 'PT. Telethama Artha Mandiri')"
                    className="h-10 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2 disabled:opacity-50">
              <GitMerge className="w-4 h-4" /> Gabung Supplier
            </button>
          )}
          {bolehTulis && (
            <button onClick={() => setForm({ mode: "create", ...emptyForm() })} data-testid="sup-add"
                    className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Tambah Supplier
            </button>
          )}
        </div>
      </div>

      {/* Peringatan nama bertagihan yang belum punya master — kasus nyata
          "TAM": teks bebas di riwayat, TIDAK PERNAH didaftarkan. Dua jalan:
          "Sinkron dari Riwayat" (buat master baru dari nama ini APA ADANYA)
          atau "Gabung" langsung ke supplier yang SUDAH benar (kalau nama ini
          cuma salah ketik/ejaan dari supplier yang sudah terdaftar). */}
      {isAdmin && (data.belum_terdaftar || []).length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 space-y-2">
          <p>
            {data.belum_terdaftar.length} nama supplier punya tagihan aktif tapi belum terdaftar di master.
            Tekan <span className="font-semibold">Sinkron dari Riwayat</span> untuk membuatnya otomatis sebagai
            master baru, atau <span className="font-semibold">Gabung</span> kalau nama ini sebenarnya supplier
            yang sudah terdaftar dengan ejaan berbeda.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {data.belum_terdaftar.map(nama => (
              <button key={nama} onClick={() => bukaMerge({ sourceNama: nama })}
                      data-testid={`sup-belum-terdaftar-gabung-${nama}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-amber-300 text-amber-800 text-[11px] font-medium hover:bg-amber-100">
                <GitMerge className="w-3 h-3" /> {nama}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Kartu ringkasan */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Supplier</p>
          <p className="font-display text-2xl font-bold mt-1.5 text-slate-900" data-testid="sup-count">
            {data.count ?? (data.items || []).length}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Supplier Berhutang</p>
          <p className="font-display text-2xl font-bold mt-1.5 text-amber-700" data-testid="sup-count-hutang">
            {berhutang}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Hutang Berjalan</p>
          <p className={`font-display text-2xl font-bold mt-1.5 ${data.total_hutang > 0 ? "text-rose-600" : "text-emerald-700"}`}
             data-testid="sup-total-hutang">
            {idr(data.total_hutang)}
          </p>
        </div>
        {/* Aging bucket (poin 8 brief owner 17 Agu 2026) — tagihan yang sudah
            menunggak lebih dari 60 hari, dijumlahkan lintas semua supplier. */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm col-span-2 lg:col-span-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3 text-rose-500" /> Menunggak &gt;60 Hari
          </p>
          <p className={`font-display text-2xl font-bold mt-1.5 ${totalMacet > 0 ? "text-rose-600" : "text-emerald-700"}`}
             data-testid="sup-total-macet">
            {idr(totalMacet)}
          </p>
        </div>
      </div>

      {/* Pencarian + toggle nonaktif */}
      <div className="flex items-center justify-between gap-3 flex-col md:flex-row">
        <div className="relative w-full md:w-72">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={e => setQ(e.target.value)} data-testid="sup-search"
                 placeholder="Cari nama / kontak / telepon..."
                 className="h-9 w-full pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <input type="checkbox" checked={lihatNonaktif} data-testid="sup-toggle-nonaktif"
                 onChange={e => setLihatNonaktif(e.target.checked)}
                 className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
          Tampilkan supplier nonaktif
        </label>
      </div>

      {/* Daftar supplier — urut hutang terbesar (dari backend) */}
      <div className="space-y-2">
        {loading ? (
          <p className="text-center text-sm text-slate-400 py-10">Memuat...</p>
        ) : items.length === 0 ? (
          <p className="text-center text-sm text-slate-400 py-10">
            Belum ada supplier{q ? " yang cocok dengan pencarian" : ""}.
            {isAdmin && !q && " Coba tombol Sinkron dari Riwayat untuk menarik nama dari tagihan yang sudah ada."}
          </p>
        ) : items.map(s => {
          const tipe = TIPE_META[s.tipe] || TIPE_META.distributor;
          const nonaktif = s.is_active === false;
          return (
            <div key={s.id} data-testid={`sup-row-${s.id}`}
                 className={`bg-white border rounded-xl p-4 shadow-sm ${nonaktif ? "border-slate-200 opacity-60" : s.hutang > 0 ? "border-rose-200" : "border-slate-200"}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-900">{s.nama}</p>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${tipe.chip}`}>{tipe.label}</span>
                    {nonaktif && s.merged_into_nama && (
                      <span data-testid={`sup-merged-into-${s.id}`}
                            title="Riwayatnya masih bisa dibuka lewat tombol Buku di bawah — hanya digabung, bukan dihapus"
                            className="text-[11px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 inline-flex items-center gap-1">
                        <GitMerge className="w-3 h-3" /> Digabung ke {s.merged_into_nama}
                      </span>
                    )}
                    {nonaktif && !s.merged_into_nama && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Nonaktif</span>
                    )}
                    {s.lead_time_hari != null ? (
                      <span data-testid={`sup-leadtime-${s.id}`}
                            title="Lead time restock — dipakai usulan stok minimum & saran qty pesan di Master Produk"
                            className="text-[11px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">
                        Lead time {s.lead_time_hari} hari
                      </span>
                    ) : (
                      <span data-testid={`sup-leadtime-${s.id}`}
                            title="Lead time belum diisi — usulan stok minimum otomatis tidak bisa dihitung untuk barang dari supplier ini"
                            className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                        Lead time belum diisi
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {[s.kontak_person, s.telepon, s.email].filter(Boolean).join(" · ") || "Kontak belum diisi"}
                  </p>
                  {s.alamat && <p className="text-xs text-slate-400 mt-0.5">{s.alamat}</p>}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Hutang Berjalan</p>
                    {s.hutang > 0 ? (
                      <p className="text-sm font-bold text-rose-600" data-testid={`sup-hutang-${s.id}`}>
                        {idr(s.hutang)}
                        <span className="font-normal text-[11px] text-slate-400 ml-1">({s.tagihan_aktif} tagihan)</span>
                      </p>
                    ) : (
                      <p className="text-sm font-bold text-emerald-700" data-testid={`sup-hutang-${s.id}`}>Lunas</p>
                    )}
                    {/* Badge umur tagihan TERPARAH (poin 8) — hanya muncul kalau
                        ada tagihan yang sudah lewat jatuh tempo (backend tidak
                        pernah mengisi ini untuk 'belum_jt'/'no_jt'/lunas). */}
                    {s.aging_bucket_terparah && AGING_TERPARAH_META[s.aging_bucket_terparah] && (
                      <span data-testid={`sup-aging-${s.id}`}
                        className={`inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${AGING_TERPARAH_META[s.aging_bucket_terparah].tone}`}>
                        {AGING_TERPARAH_META[s.aging_bucket_terparah].label}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => bukaBuku(s)} data-testid={`sup-buku-${s.id}`}
                            className="h-9 px-3 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm font-medium inline-flex items-center gap-1.5">
                      <BookOpen className="w-4 h-4" /> Buku
                    </button>
                    {/* Sudah pernah jadi SUMBER merge -> backend menolak dipakai
                        sumber lagi (anti rantai), jadi tombolnya disembunyikan
                        di sini juga (bukan cuma menunggu 403). */}
                    {isAdmin && !s.merged_into && (
                      <button onClick={() => bukaMerge({ sourceId: s.id, sourceNama: s.nama })}
                              data-testid={`sup-merge-row-${s.id}`} title="Gabungkan supplier ini ke supplier lain"
                              className="h-9 w-9 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center">
                        <GitMerge className="w-4 h-4" />
                      </button>
                    )}
                    {bolehUbah && !nonaktif && (
                      <button onClick={() => setForm({ mode: "edit", id: s.id, punya_riwayat: s.punya_riwayat, ...emptyForm(), ...s })}
                              data-testid={`sup-edit-${s.id}`}
                              className="h-9 w-9 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center">
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {bolehUbah && nonaktif && (
                      <button onClick={() => aktifkan(s)} disabled={busy} data-testid={`sup-aktifkan-${s.id}`}
                              className="h-9 px-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                        <Power className="w-4 h-4" /> Aktifkan
                      </button>
                    )}
                    {bolehHapus && !nonaktif && (
                      <button onClick={() => hapus(s)} disabled={busy} data-testid={`sup-hapus-${s.id}`}
                              title={s.punya_riwayat ? "Punya riwayat tagihan — akan dinonaktifkan" : "Hapus"}
                              className="h-9 w-9 rounded-lg border border-slate-200 text-rose-500 hover:bg-rose-50 inline-flex items-center justify-center disabled:opacity-50">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Form tambah / edit */}
      {form && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             onClick={() => setForm(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <p className="font-display font-bold text-base text-slate-900">
                {form.mode === "edit" ? "Edit Supplier" : "Tambah Supplier"}
              </p>
              <button onClick={() => setForm(null)} data-testid="sup-f-close"
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Nama Supplier <span className="text-rose-500">*</span></label>
                <input value={form.nama} data-testid="sup-f-nama"
                       disabled={form.mode === "edit" && form.punya_riwayat}
                       placeholder="cth. PT Distributor Gadget Nusantara"
                       onChange={e => setForm(f => ({ ...f, nama: e.target.value }))}
                       className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-slate-50 disabled:text-slate-500" />
                {form.mode === "edit" && form.punya_riwayat && (
                  <p className="text-[11px] text-amber-700 mt-1">
                    Nama terkunci karena sudah punya riwayat tagihan — nama adalah kunci penghubung ke buku hutang.
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Tipe</label>
                  <select value={form.tipe} data-testid="sup-f-tipe"
                          onChange={e => setForm(f => ({ ...f, tipe: e.target.value }))}
                          className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
                    <option value="distributor">Distributor</option>
                    <option value="perorangan">Perorangan</option>
                    <option value="toko_lain">Toko Lain</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Telepon / WhatsApp</label>
                  <input value={form.telepon} data-testid="sup-f-telepon" inputMode="tel"
                         placeholder="08xxxxxxxxxx"
                         onChange={e => setForm(f => ({ ...f, telepon: e.target.value }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Kontak Person</label>
                  <input value={form.kontak_person} data-testid="sup-f-kontak"
                         placeholder="cth. Pak Hendra (sales)"
                         onChange={e => setForm(f => ({ ...f, kontak_person: e.target.value }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Email</label>
                  <input value={form.email} data-testid="sup-f-email" type="email"
                         placeholder="opsional"
                         onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Alamat</label>
                <input value={form.alamat} data-testid="sup-f-alamat"
                       placeholder="opsional"
                       onChange={e => setForm(f => ({ ...f, alamat: e.target.value }))}
                       className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Lead Time Restock (hari)</label>
                <input value={form.lead_time_hari ?? ""} data-testid="sup-f-leadtime"
                       type="number" min="0" inputMode="numeric"
                       placeholder="belum diketahui — kosongkan kalau belum tahu"
                       onChange={e => setForm(f => ({ ...f, lead_time_hari: e.target.value }))}
                       className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                <p className="text-[11px] text-slate-500 mt-1">
                  Berapa hari sejak PO dibuat sampai barang biasanya datang — dipakai menghitung usulan
                  stok minimum & saran qty pesan otomatis di Master Produk.
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Catatan</label>
                <textarea value={form.catatan} rows={2} data-testid="sup-f-catatan"
                          placeholder="cth. minimal order 10 unit, tempo 30 hari"
                          onChange={e => setForm(f => ({ ...f, catatan: e.target.value }))}
                          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
              <button onClick={() => setForm(null)}
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">
                Batal
              </button>
              <button onClick={simpan} disabled={busy || !form.nama.trim()} data-testid="sup-f-save"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {busy ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drawer buku besar */}
      {drawer && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex justify-end" onClick={() => setDrawer(null)}>
          <div className="bg-white h-full w-full max-w-2xl shadow-xl flex flex-col"
               onClick={e => e.stopPropagation()} data-testid="sup-drawer">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div>
                <p className="font-display font-bold text-base text-slate-900 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-emerald-600" /> Buku Supplier — {drawer.supplier.nama}
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Saldo negatif (merah) = perusahaan berhutang. Bayar lewat{" "}
                  <Link to="/finance/utang" className="text-emerald-700 hover:underline"
                        data-testid="sup-drawer-link-utang">Finance &gt; Utang</Link>.
                </p>
              </div>
              <button onClick={() => setDrawer(null)} data-testid="sup-drawer-close"
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Filter tanggal + aksi statement */}
            <div className="px-5 py-3 border-b border-slate-100 flex items-end gap-2 flex-wrap">
              <div>
                <label className="text-[11px] font-medium text-slate-500">Dari</label>
                <input type="date" value={drawer.date_from} data-testid="sup-buku-from"
                       onChange={e => bukaBuku(drawer.supplier, e.target.value, drawer.date_to)}
                       className="block mt-0.5 h-9 px-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-500">Sampai</label>
                <input type="date" value={drawer.date_to} data-testid="sup-buku-to"
                       onChange={e => bukaBuku(drawer.supplier, drawer.date_from, e.target.value)}
                       className="block mt-0.5 h-9 px-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div className="flex-1" />
              <button onClick={salinStatement} disabled={busy} data-testid="sup-statement-copy"
                      title="Salin teks statement"
                      className="h-9 w-9 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center disabled:opacity-50">
                <Copy className="w-4 h-4" />
              </button>
              <button onClick={kirimStatement} disabled={busy} data-testid="sup-statement-wa"
                      className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                <MessageCircle className="w-4 h-4" /> Kirim Statement via WA
              </button>
            </div>

            {/* Isi buku */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {drawer.loading || !buku ? (
                <p className="text-center text-sm text-slate-400 py-10">Memuat buku...</p>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Tagihan Periode</p>
                      <p className="text-sm font-bold text-slate-900 mt-1">{idr(buku.total_tagihan)}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Pembayaran Periode</p>
                      <p className="text-sm font-bold text-emerald-700 mt-1">{idr(buku.total_pembayaran)}</p>
                    </div>
                    <div className={`rounded-lg p-3 ${buku.label === "Hutang" ? "bg-rose-50" : "bg-emerald-50"}`}>
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Saldo Akhir</p>
                      <p className={`text-sm font-bold mt-1 ${buku.label === "Hutang" ? "text-rose-600" : "text-emerald-700"}`}
                         data-testid="sup-saldo-akhir">
                        {idr(Math.abs(buku.saldo_akhir))} · {buku.label}
                      </p>
                    </div>
                  </div>

                  <div className="overflow-x-auto border border-slate-200 rounded-xl">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                          <th className="px-3 py-2 font-semibold">Tanggal</th>
                          <th className="px-3 py-2 font-semibold">Mutasi</th>
                          <th className="px-3 py-2 font-semibold">Keterangan</th>
                          <th className="px-3 py-2 font-semibold text-right">Nominal</th>
                          <th className="px-3 py-2 font-semibold text-right">Saldo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {drawer.date_from && (
                          <tr className="border-t border-slate-100 bg-slate-50/50">
                            <td className="px-3 py-2 text-slate-500" colSpan={4}>Saldo awal periode</td>
                            <td className={`px-3 py-2 text-right font-mono font-medium ${buku.opening_balance < 0 ? "text-rose-600" : "text-slate-700"}`}>
                              {idr(buku.opening_balance)}
                            </td>
                          </tr>
                        )}
                        {buku.entries.length === 0 ? (
                          <tr className="border-t border-slate-100">
                            <td className="px-3 py-6 text-center text-slate-400" colSpan={5}>
                              Tidak ada mutasi pada periode ini.
                            </td>
                          </tr>
                        ) : buku.entries.map((e, i) => (
                          <tr key={`${e.ref}-${i}`} className="border-t border-slate-100">
                            <td className="px-3 py-2 whitespace-nowrap text-slate-600">{e.tanggal}</td>
                            <td className="px-3 py-2">
                              <span className={`text-[11px] px-2 py-0.5 rounded-full ${e.jenis === "tagihan" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-800"}`}>
                                {e.jenis === "tagihan" ? "Tagihan" : "Pembayaran"}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-slate-700">
                              <span className="font-mono text-[11px] text-slate-400 mr-1.5">{e.ref}</span>
                              {e.keterangan}
                            </td>
                            <td className={`px-3 py-2 text-right font-mono whitespace-nowrap ${e.mutasi < 0 ? "text-rose-600" : "text-emerald-700"}`}>
                              {e.mutasi < 0 ? "-" : "+"}{idr(Math.abs(e.mutasi))}
                            </td>
                            <td className={`px-3 py-2 text-right font-mono whitespace-nowrap font-medium ${e.saldo < 0 ? "text-rose-600" : "text-slate-700"}`}>
                              {idr(e.saldo)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Merge Supplier — DUA TAHAP: pratinjau dulu, tombol eksekusi
          baru aktif setelah pratinjau berhasil DAN tidak ada tabrakan. Ubah
          sumber/target APA PUN membatalkan pratinjau (lihat `ubahMerge`),
          supaya staf tidak bisa mengeksekusi hasil pratinjau yang sudah basi. */}
      {mergeModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             onClick={() => !mergeModal.executing && setMergeModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()} data-testid="sup-merge-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <p className="font-display font-bold text-base text-slate-900 flex items-center gap-2">
                <GitMerge className="w-4 h-4 text-emerald-600" /> Gabung Supplier
              </p>
              <button onClick={() => setMergeModal(null)} data-testid="sup-merge-close"
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <p className="text-xs text-slate-500">
                Sumber akan <span className="font-semibold">dinonaktifkan</span> (bukan dihapus) dan seluruh
                riwayatnya (PO, penerimaan, utang, unit, retur, Credit Note, Cashback) dipindahkan ke target.
                Uang tidak berubah — hanya identitasnya disatukan.
              </p>

              {/* Sumber */}
              <div>
                <label className="text-xs font-medium text-slate-600">Sumber (akan dinonaktifkan)</label>
                <div className="flex gap-2 mt-1">
                  <select value={mergeModal.sourceType} data-testid="sup-merge-source-type"
                          onChange={e => ubahMerge({ sourceType: e.target.value, sourceId: "", sourceNama: "" })}
                          className="h-10 px-2 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
                    <option value="master">Master terdaftar</option>
                    <option value="nama">Nama dari riwayat (belum terdaftar)</option>
                  </select>
                  {mergeModal.sourceType === "master" ? (
                    <select value={mergeModal.sourceId} data-testid="sup-merge-source-select"
                            onChange={e => ubahMerge({ sourceId: e.target.value })}
                            className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
                      <option value="">— pilih supplier —</option>
                      {(data.items || []).filter(s => s.is_active !== false && !s.merged_into).map(s => (
                        <option key={s.id} value={s.id}>{s.nama}</option>
                      ))}
                    </select>
                  ) : (
                    <input value={mergeModal.sourceNama} data-testid="sup-merge-source-nama"
                           list="sup-merge-belum-terdaftar"
                           placeholder="cth. TAM (persis seperti tertulis di riwayat)"
                           onChange={e => ubahMerge({ sourceNama: e.target.value })}
                           className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                  )}
                </div>
                {mergeModal.sourceType === "nama" && (
                  <datalist id="sup-merge-belum-terdaftar">
                    {(data.belum_terdaftar || []).map(n => <option key={n} value={n} />)}
                  </datalist>
                )}
              </div>

              {/* Target */}
              <div>
                <label className="text-xs font-medium text-slate-600">Target (tujuan penggabungan)</label>
                <select value={mergeModal.targetId} data-testid="sup-merge-target-select"
                        onChange={e => ubahMerge({ targetId: e.target.value })}
                        className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
                  <option value="">— pilih supplier target —</option>
                  {(data.items || []).filter(s => s.is_active !== false && s.id !== mergeModal.sourceId).map(s => (
                    <option key={s.id} value={s.id}>{s.nama}</option>
                  ))}
                </select>
              </div>

              <button onClick={previewMerge} disabled={mergeModal.previewLoading} data-testid="sup-merge-preview-btn"
                      className="h-10 px-4 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm font-medium w-full disabled:opacity-50">
                {mergeModal.previewLoading ? "Memuat pratinjau..." : "Lihat Pratinjau"}
              </button>

              {/* Hasil pratinjau — TIDAK menulis apa pun ke database */}
              {mergeModal.preview && (
                <div className="border border-slate-200 rounded-xl p-3 space-y-3" data-testid="sup-merge-preview-result">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-slate-50 rounded-lg p-2">
                      <p className="text-[10px] uppercase text-slate-500 font-semibold">Hutang Sumber</p>
                      <p className="text-sm font-bold text-slate-900">
                        {idr(mergeModal.preview.ringkasan_utang.sumber.total_hutang_aktif)}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2">
                      <p className="text-[10px] uppercase text-slate-500 font-semibold">Hutang Target</p>
                      <p className="text-sm font-bold text-slate-900">
                        {idr(mergeModal.preview.ringkasan_utang.target.total_hutang_aktif)}
                      </p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-2">
                      <p className="text-[10px] uppercase text-slate-500 font-semibold">Gabungan (setelah)</p>
                      <p className="text-sm font-bold text-emerald-700" data-testid="sup-merge-total-gabungan">
                        {idr(mergeModal.preview.ringkasan_utang.total_gabungan)}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="text-[11px] font-semibold text-slate-500 uppercase mb-1">
                      Dokumen yang akan dipindahkan ({mergeModal.preview.total_dokumen})
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {mergeModal.preview.dampak.filter(d => d.jumlah > 0).map(d => (
                        <span key={d.koleksi} className="text-[11px] px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                          {d.koleksi}: {d.jumlah}
                        </span>
                      ))}
                      {mergeModal.preview.total_dokumen === 0 && (
                        <span className="text-[11px] text-slate-400">Tidak ada dokumen yang tersentuh.</span>
                      )}
                    </div>
                  </div>

                  {mergeModal.preview.tabrakan.length > 0 ? (
                    <div className="bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="sup-merge-tabrakan">
                      <p className="text-xs font-semibold text-rose-800 flex items-center gap-1.5 mb-1.5">
                        <XCircle className="w-4 h-4" /> {mergeModal.preview.tabrakan.length} tabrakan nomor dokumen — eksekusi diblokir
                      </p>
                      <ul className="space-y-1">
                        {mergeModal.preview.tabrakan.map((t, i) => (
                          <li key={i} className="text-[11px] text-rose-700">{t.pesan}</li>
                        ))}
                      </ul>
                      <p className="text-[11px] text-rose-600 mt-1.5">
                        Selesaikan dulu di data aslinya (ganti/hapus salah satu nomor yang bentrok), lalu pratinjau ulang.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 flex items-center gap-1.5 text-xs text-emerald-800">
                      <CheckCircle2 className="w-4 h-4" /> Tidak ada tabrakan — siap digabung.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
              <button onClick={() => setMergeModal(null)} disabled={mergeModal.executing}
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                Batal
              </button>
              <button onClick={eksekusiMerge} data-testid="sup-merge-execute-btn"
                      disabled={!mergeModal.preview?.bisa_eksekusi || mergeModal.executing}
                      title={!mergeModal.preview ? "Lihat pratinjau dulu" : undefined}
                      className="h-10 px-4 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium disabled:opacity-50 disabled:bg-slate-300">
                {mergeModal.executing ? "Menggabungkan..." : "Eksekusi Gabung"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
